import {
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useMemo,
  type MutableRefObject,
  type SyntheticEvent,
} from 'react';
import { isAxiosError } from 'axios';
import { IS_WAPI_WIZARD_SIMULATOR_ENABLED } from 'src/constants/envs';
import { WHATSAPP_SDK_TIMEOUT_ERROR_CODE } from 'src/constants/whatsappBusinessErrorCodes.constants';
import {
  clearFacebookSDKTimeout,
  FACEBOOK_SDK_READY_TIMEOUT_MS,
  waitForFacebookSDK,
} from 'src/lib/facebook/facebookSDKReadiness';
import {
  buildEmbeddedSignupLoginOptions,
  withPopupPinnedToLeftEdge,
  type tEmbeddedSignupLoginOptions,
  type tEmbeddedSignupPath,
} from 'src/lib/facebook/embeddedSignupLogin';
import { LLog, LOG_COMPONENTS, LOG_OPERATIONS } from 'src/lib/logging';
import { useIntegrationsContext } from 'src/providers/IntegrationsProvider/Integrations.context';
import { eIntegrationStatus } from 'src/providers/IntegrationsProvider/Integrations.enums';
import { useSocketsContext } from 'src/providers/SocketsProvider/Sockets.context';
import { DashboardService } from 'src/pages/Dashboard/Dashboard.service';
import type { tFBLoginResponse } from 'src/pages/Dashboard/Dashboard.types';
import {
  connectionReducer,
  createInitialState,
  type tConnectionAction,
  type tEmbeddedSignupErrorReport,
  readConnectVerdict,
  toObservedActivation,
  WAPI_DEV_SCENARIO_CLOSED_STEP,
  WapiExchangeTimeoutError,
} from 'src/hooks/whatsapp/whatsappConnect.types';
import {
  clearWapiState,
  getWapiStateKey,
  readWapiState,
  writeWapiState,
} from 'src/hooks/whatsapp/wapiStateStorage';
import { useEmbeddedSignupSession } from 'src/hooks/whatsapp/useEmbeddedSignupSession.hook';
import { useEmbeddedSignupTelemetry } from 'src/hooks/whatsapp/useEmbeddedSignupTelemetry.hook';
import {
  beginEmbeddedSignupAttempt,
  finalizeEmbeddedSignupAttempt,
  installEmbeddedSignupTelemetry,
  registerEmbeddedSignupSink,
  type tEmbeddedSignupRecord,
  type tEmbeddedSignupSurface,
} from 'src/lib/facebook/embeddedSignupTelemetry';

// Upper bound on how long we wait for the backend code exchange before
// surfacing a FAIL_ERROR. Without this, a hung BFF/leads-service leaves
// pendingRef pinned true and disables every subsequent retry click within
// the session.
const AUTHENTICATE_TIMEOUT_MS = 45_000;

export type tWapiFailureReason =
  | 'api_error'
  | 'popup_closed'
  | 'sdk_error'
  | 'sdk_timeout';

/** Screens `__devSimulate` can preview while the screen simulator is enabled. */
export type tWapiDevScenario =
  | 'before_connect'
  | 'success'
  | 'success_without_history'
  | 'fail_error'
  | 'fail_error_again'
  | 'fail_closed'
  | 'meta_rate_limited'
  | 'closed_at_save'
  | 'activating'
  | 'activating_over_3_seconds'
  | 'activating_over_30_seconds'
  | 'activation_pending'
  | 'activation_pending_over_an_hour'
  | 'activation_failed'
  | 'activation_failed_again'
  | 'activation_failed_lost_access'
  | 'activation_failed_other_reason'
  | 'meta_window_open'
  | 'reported_2655123_window_open'
  | 'reported_2655123_window_closed'
  | 'reported_2655093_window_open'
  | 'reported_2655093_window_closed'
  | 'reported_2655121_window_closed'
  | 'reported_4563004_window_closed'
  | 'reported_3441034_window_closed'
  | 'reported_3441038_window_closed'
  | 'reported_3441061_window_closed'
  | 'reported_1675012_window_closed'
  | 'reported_2388091_window_closed'
  | 'reported_2494064_window_closed'
  | 'reported_unknown_window_open'
  | 'reported_unknown_window_closed'
  | 'closed_at_step'
  | 'closed_again_at_step';

/** An error report out of the wizard's catalog, with Meta's text. */
const DEV_SCENARIO_UNKNOWN_ERROR_REPORT: tEmbeddedSignupErrorReport = {
  code: 2388386,
  message:
    'Para usar este número de teléfono, deberás eliminar alguno de Meta Business Suite.',
};

/** The report each reported-error scenario sends, and whether Meta's window then closes. */
const DEV_SCENARIO_ERROR_REPORTS: Partial<
  Record<
    tWapiDevScenario,
    { errorReport: tEmbeddedSignupErrorReport; closesWindow: boolean }
  >
> = {
  reported_2655123_window_open: {
    errorReport: { code: 2655123, message: null },
    closesWindow: false,
  },
  reported_2655123_window_closed: {
    errorReport: { code: 2655123, message: null },
    closesWindow: true,
  },
  reported_2655093_window_open: {
    errorReport: { code: 2655093, message: null },
    closesWindow: false,
  },
  reported_2655093_window_closed: {
    errorReport: { code: 2655093, message: null },
    closesWindow: true,
  },
  reported_2655121_window_closed: {
    errorReport: { code: 2655121, message: null },
    closesWindow: true,
  },
  reported_4563004_window_closed: {
    errorReport: { code: 4563004, message: null },
    closesWindow: true,
  },
  reported_3441034_window_closed: {
    errorReport: { code: 3441034, message: null },
    closesWindow: true,
  },
  reported_3441038_window_closed: {
    errorReport: { code: 3441038, message: null },
    closesWindow: true,
  },
  reported_3441061_window_closed: {
    errorReport: { code: 3441061, message: null },
    closesWindow: true,
  },
  reported_1675012_window_closed: {
    errorReport: { code: 1675012, message: null },
    closesWindow: true,
  },
  reported_2388091_window_closed: {
    errorReport: { code: 2388091, message: null },
    closesWindow: true,
  },
  reported_2494064_window_closed: {
    errorReport: { code: 2494064, message: null },
    closesWindow: true,
  },
  reported_unknown_window_open: {
    errorReport: DEV_SCENARIO_UNKNOWN_ERROR_REPORT,
    closesWindow: false,
  },
  reported_unknown_window_closed: {
    errorReport: DEV_SCENARIO_UNKNOWN_ERROR_REPORT,
    closesWindow: true,
  },
};

export type tWapiConnectAnalytics = {
  /** Receives the path the attempt launched with; absent when it declared none. */
  onStarted?: (path?: tEmbeddedSignupPath) => void;
  /**
   * INT-1516 — fires only when the connect response's verdict is operational.
   * A held or failed 200 fires nothing here, and neither does a hold that later
   * turns CONNECTED on the integration row.
   */
  onSuccess?: () => void;
  onError?: (reason: tWapiFailureReason, err?: Error) => void;
  onSkipped?: () => void;
};

export type tUseWhatsAppConnectStateOptions = {
  workspaceId: string | undefined;
  analytics?: tWapiConnectAnalytics;
  /**
   * Fires when the success countdown reaches 0. Onboarding navigates to the
   * funnel page; the Dashboard modal closes itself.
   */
  onSuccess: () => void;
  /**
   * When true, dispatches SUCCESS automatically on mount if the workspace's
   * WhatsApp Business integration is already CONNECTED. Onboarding wants this
   * (mobile fallback when the user lands here post-Embedded-Signup). The
   * Dashboard modal should pass false — otherwise reopening the modal over
   * an already-connected integration immediately auto-closes via the success
   * countdown. Defaults to true to preserve the original Onboarding behavior.
   */
  autoSuccessOnAlreadyConnected?: boolean;
  /**
   * INT-1216 — launch surface recorded on the Embedded Signup attempt so the
   * abandonment funnel can tell onboarding apart from the dashboard modal.
   * Optional on purpose: a caller that declares nothing produces a `null`
   * surface rather than being silently mislabelled with a default.
   */
  surface?: tEmbeddedSignupSurface;
};

/** Options for `launchConnection`. */
export type tLaunchConnectionOptions = {
  /** The Embedded Signup flow Meta opens; `coexistence` when omitted. */
  path?: tEmbeddedSignupPath;
  /**
   * Opens the Meta popup against the left edge of the browser window, over the
   * main panel and clear of the aside with the steps; centered when omitted.
   */
  pinPopupToLeftEdge?: boolean;
};

/** Clears the exchange's wait timer, if one is running. */
function clearExchangeTimeout(
  timeoutRef: MutableRefObject<ReturnType<typeof setTimeout> | null>,
) {
  if (timeoutRef.current === null) return;
  clearTimeout(timeoutRef.current);
  timeoutRef.current = null;
}

/**
 * Rewrites the stored entry without its failure cause, keeping its phase,
 * counters, time and path, which a reload during the new attempt restores.
 */
function dropStoredFailureCause(workspaceId: string) {
  const stored = readWapiState(workspaceId);
  if (!stored) return;
  writeWapiState(workspaceId, {
    phase: stored.phase,
    retryCount: stored.retryCount,
    hasSkippedBefore: stored.hasSkippedBefore,
    lastAttemptAt: stored.lastAttemptAt,
    path: stored.path,
  });
}

/**
 * Reads the chosen path from `launchConnection`'s argument. `retry` is the same
 * function and some screens hand it straight to `onClick`, so a click event can
 * arrive as the argument: only a known path value is taken, anything else
 * falls back to the builder's default.
 */
function readEmbeddedSignupPath(
  launchOptions: tLaunchConnectionOptions | SyntheticEvent | undefined,
): tEmbeddedSignupPath | undefined {
  if (!launchOptions || !('path' in launchOptions)) return undefined;
  const { path } = launchOptions;
  return path === 'coexistence' || path === 'cloud_api' ? path : undefined;
}

/**
 * Reads whether to pin the Meta popup from `launchConnection`'s argument. A
 * click event can arrive as the argument too, so only a literal `true` pins it.
 */
function readPinPopupToLeftEdge(
  launchOptions: tLaunchConnectionOptions | SyntheticEvent | undefined,
): boolean {
  if (!launchOptions || !('pinPopupToLeftEdge' in launchOptions)) return false;
  return launchOptions.pinPopupToLeftEdge === true;
}

/** Dispatches the actions that take the reducer to a simulated screen. */
function dispatchDevScenario(
  dispatch: (action: tConnectionAction) => void,
  scenario: tWapiDevScenario,
) {
  // RESET, never the hook's `reset`: that one also clears the stored entry.
  if (scenario === 'before_connect') return dispatch({ type: 'RESET' });
  dispatch({ type: 'START_CONNECTING' });
  const reported = DEV_SCENARIO_ERROR_REPORTS[scenario];
  if (reported) {
    dispatch({
      type: 'EMBEDDED_SIGNUP_REPORTED',
      step: null,
      errorReport: reported.errorReport,
    });
    if (reported.closesWindow) dispatch({ type: 'FAIL_CLOSED' });
    return;
  }
  switch (scenario) {
    // Whether the history was shared is the consumer's to show.
    case 'success':
    case 'success_without_history':
      return dispatch({ type: 'SUCCESS' });
    // How long the wait has run, and earlier failures, are the consumer's to
    // show: the reducer state of these scenes is the same.
    case 'activating':
    case 'activating_over_3_seconds':
    case 'activating_over_30_seconds':
      return dispatch({ type: 'META_RESPONDED' });
    case 'activation_pending':
    case 'activation_pending_over_an_hour':
      return dispatch({ type: 'VERIFY', reason: 'cloud_api_account_missing' });
    case 'activation_failed':
    case 'activation_failed_again':
      return dispatch({
        type: 'FAIL_ACTIVATION',
        reason: 'cloud_api_account_missing',
      });
    case 'activation_failed_lost_access':
      return dispatch({ type: 'FAIL_ACTIVATION', reason: 'waba_not_visible' });
    case 'activation_failed_other_reason':
      return dispatch({ type: 'FAIL_ACTIVATION', reason: 'other' });
    // Earlier errors are the consumer's to show: the reducer state of these
    // scenes is the same.
    case 'fail_error':
    case 'fail_error_again':
      return dispatch({
        type: 'FAIL_ERROR',
        error: 'Simulated Meta API error',
      });
    case 'fail_closed':
      return dispatch({ type: 'FAIL_CLOSED' });
    case 'meta_rate_limited':
      dispatch({
        type: 'EMBEDDED_SIGNUP_REPORTED',
        step: null,
        errorReport: {
          code: 133006,
          message: 'Demasiados intentos de registro de teléfono',
        },
      });
      return dispatch({ type: 'FAIL_CLOSED' });
    case 'closed_at_save':
      dispatch({
        type: 'EMBEDDED_SIGNUP_REPORTED',
        step: 'SAVE',
        errorReport: null,
      });
      return dispatch({ type: 'FAIL_CLOSED' });
    // Meta's window stays open: the attempt alone is the screen.
    case 'meta_window_open':
      return;
    case 'closed_at_step':
    case 'closed_again_at_step':
      dispatch({
        type: 'EMBEDDED_SIGNUP_REPORTED',
        step: WAPI_DEV_SCENARIO_CLOSED_STEP,
        errorReport: null,
      });
      return dispatch({ type: 'FAIL_CLOSED' });
  }
}

export function useWhatsAppConnectState(options: tUseWhatsAppConnectStateOptions) {
  const { workspaceId, analytics, onSuccess, autoSuccessOnAlreadyConnected = true, surface } =
    options;
  const [state, dispatch] = useReducer(connectionReducer, workspaceId, createInitialState);
  const { whatsappBusinessIntegration, refetchIntegrations } = useIntegrationsContext();
  const { workspaceAccountUpdateEvent } = useSocketsContext();
  const pendingRef = useRef(false);
  const mountedRef = useRef(true);
  const sdkTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const exchangeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // INT-1516: the connect response resolves long after the render that created
  // its callback, so it reads the provider's refetch through a latest-value ref.
  const refetchIntegrationsRef = useRef(refetchIntegrations);
  refetchIntegrationsRef.current = refetchIntegrations;
  // Captures waba_id / phone_number_id from the WA_EMBEDDED_SIGNUP sessionInfo
  // so we can forward them to the backend (required for Coexistence).
  const { sessionInfoRef, reset: resetSessionInfo } = useEmbeddedSignupSession();
  // INT-1216: emits the abandonment / error-report records Meta posts during the
  // popup. Independent of sessionInfoRef above — that one feeds the connection,
  // this one feeds the funnel.
  useEmbeddedSignupTelemetry();

  // A simulation persists nothing, so the stored entry still holds the real
  // state. Every real action or event, except "Regresar", restores it first,
  // so it starts from the real counters and its outcome is persisted and
  // tracked again. Refs, so the effects that call this keep their keys.
  const isSimulatedRef = useRef(state.isSimulated);
  isSimulatedRef.current = state.isSimulated;
  const workspaceIdRef = useRef(workspaceId);
  workspaceIdRef.current = workspaceId;
  const leaveSimulation = useCallback(() => {
    if (!isSimulatedRef.current) return false;
    isSimulatedRef.current = false;
    dispatch({
      type: 'LEAVE_SIMULATION',
      realState: createInitialState(workspaceIdRef.current),
    });
    return true;
  }, []);

  // Cleanup on unmount — guards all async dispatches and clears both wait
  // timers, so neither outlives the consumer.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearFacebookSDKTimeout(sdkTimeoutRef);
      clearExchangeTimeout(exchangeTimeoutRef);
    };
  }, []);

  // INT-1217: mirror what Meta reported about the popup into connection state so
  // the failure screens can render step-specific guidance instead of leading
  // with "agenda una sesión".
  //
  // Registered WITHOUT a key, deliberately inverting the keyed pattern the
  // PostHog and HTTP sinks use. Those two emit outward and must fire exactly
  // once per record, so they key to dedupe across simultaneous mounts. This sink
  // only writes into its own component's reducer. The Dashboard route mounts two
  // consumers of this hook at the same time, and a key would deliver the record
  // to the newest registration only — silently starving whichever registered
  // first, most likely the one actually rendering the modal. Unkeyed sinks all
  // stack, so every mounted instance updates its own state and none goes dark.
  //
  // Records that arrive after `finalizeEmbeddedSignupAttempt()` never reach any
  // sink (see `handleMessage`), so guidance depends on Meta's postMessage
  // preceding the FB.login callback — the ordering the popup actually produces.
  useEffect(() => {
    // Idempotent; called here too so this sink does not depend on the order in
    // which sibling hooks' effects happen to run.
    installEmbeddedSignupTelemetry();

    function toConnectionState(record: tEmbeddedSignupRecord) {
      if (!mountedRef.current) return;
      dispatch({
        type: 'EMBEDDED_SIGNUP_REPORTED',
        step: record.currentStep,
        errorReport:
          record.kind === 'error_report'
            ? { code: record.errorCode, message: record.errorMessage }
            : null,
      });
    }

    return registerEmbeddedSignupSink(toConnectionState);
  }, []);

  const isAlreadyConnected = useMemo(
    () => whatsappBusinessIntegration?.state === eIntegrationStatus.CONNECTED,
    [whatsappBusinessIntegration?.state],
  );

  // Mobile / re-entry fallback: if the integration is already connected when
  // the component mounts, skip the connect step. Opt-in per surface — the
  // Dashboard modal disables this so the user can reopen the modal to re-watch
  // the tutorial without auto-triggering the success countdown.
  useEffect(() => {
    if (!autoSuccessOnAlreadyConnected) return;
    if (isAlreadyConnected) {
      dispatch({ type: 'SUCCESS' });
    }
  }, [isAlreadyConnected, autoSuccessOnAlreadyConnected]);

  // INT-1516: the integration row is the verdict's source of truth once the
  // connect response is in. A change ends a hold (CONNECTED → success), shows a
  // failure the refetch found (ACTIVATION_FAILED), and on mount restores the
  // verdict phases, which are never persisted.
  //
  // Keyed on the row's CHANGES, never re-run on a phase change. Right after a
  // held 200 the unchanged row still describes the record this connect
  // replaced (typically ACTIVATION_FAILED, since that screen is where the
  // customer reconnects from), and a refetch answered by a replica that has not
  // caught up returns the same row. Acting on it would send the customer
  // straight back to reconnect. It also lets "Regresar" leave activation_failed
  // without the row pulling the dialog back.
  const observedActivation = toObservedActivation(whatsappBusinessIntegration?.state);
  const observedReason = whatsappBusinessIntegration?.reason ?? null;
  useEffect(() => {
    leaveSimulation();
    dispatch({
      type: 'ACTIVATION_OBSERVED',
      activation: observedActivation,
      reason: observedReason,
    });
  }, [observedActivation, observedReason, leaveSimulation]);

  useEffect(() => {
    if (!workspaceId) return;
    const targetKey = getWapiStateKey(workspaceId);
    function onStorage(event: StorageEvent) {
      if (event.key !== targetKey || !event.newValue) return;
      const persisted = readWapiState(workspaceId!);
      if (!persisted) return;
      leaveSimulation();
      dispatch({
        type: 'RESYNC_FROM_STORAGE',
        retryCount: persisted.retryCount,
        phase: persisted.phase,
        lastAttemptAt: persisted.lastAttemptAt,
        errorCode: persisted.errorCode,
        signupStep: persisted.signupStep,
        signupErrorReport: persisted.signupErrorReport,
        path: persisted.path,
      });
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [workspaceId, leaveSimulation]);

  workspaceAccountUpdateEvent.useSubscription((value) => {
    if (!mountedRef.current) return;
    if (!(value.connected && value.integration_type === 'whatsapp_api')) return;
    // Race protection: if an HTTP authenticate call is in flight (pendingRef),
    // let its outcome decide SUCCESS vs FAIL — otherwise a stale socket event
    // (e.g. workspace heartbeat replay, late-arriving event from a prior
    // session) can win the race and paint a success screen for an API call
    // that ultimately rejects (and whose FAIL_ERROR is then dropped by the
    // reducer's phase guard).
    if (pendingRef.current) return;
    // INT-1216: a socket-driven success is a terminal outcome too. Without this
    // a popup dismissed after the socket already confirmed the connection would
    // be recorded as an abandonment.
    finalizeEmbeddedSignupAttempt();
    leaveSimulation();
    dispatch({ type: 'SUCCESS' });
  });

  // Persist phase to the shared wapi state helper on change. Transient phases
  // (connecting/verifying) are not persisted so a hard reload during the SDK
  // popup does not strand the UI in an in-between state. INT-1516: neither is
  // activation_failed. Both verdict phases come from the integration row, which
  // a reload reads again, so a stored copy could only go stale.
  //
  // The failure's cause and the attempt's path are stored with the phase, so a
  // failure screen restored after a reload still names its cause and a retry
  // opens the same flow. Only a failed attempt carries a cause in state.
  //
  // A simulated screen is never persisted, so it cannot replace a real stored
  // attempt or reach another tab.
  useEffect(() => {
    if (!workspaceId || state.isSimulated) return;
    if (
      state.phase === 'connecting' ||
      state.phase === 'verifying' ||
      state.phase === 'activation_failed'
    )
      return;
    // After SUCCESS, drop the entry so the next connection attempt starts clean.
    if (state.phase === 'success') {
      clearWapiState(workspaceId);
      return;
    }
    writeWapiState(workspaceId, {
      phase: state.phase === 'escalated' ? 'failed_closed' : state.phase,
      retryCount: state.retryCount,
      hasSkippedBefore: state.hasSkippedBefore,
      lastAttemptAt: state.lastAttemptAt,
      errorCode: state.errorCode ?? undefined,
      signupStep: state.signupStep ?? undefined,
      signupErrorReport: state.signupErrorReport ?? undefined,
      path: state.path,
    });
  }, [
    state.phase,
    state.retryCount,
    state.hasSkippedBefore,
    state.lastAttemptAt,
    state.errorCode,
    state.signupStep,
    state.signupErrorReport,
    state.path,
    state.isSimulated,
    workspaceId,
  ]);

  // Countdown timer once we reach success.
  useEffect(() => {
    if (state.phase !== 'success') return;
    const interval = setInterval(() => dispatch({ type: 'TICK' }), 1_000);
    return () => clearInterval(interval);
  }, [state.phase]);

  // Trigger consumer onSuccess after countdown reaches 0.
  useEffect(() => {
    if (state.phase === 'success' && state.countdown === 0) {
      onSuccess();
    }
  }, [state.phase, state.countdown, onSuccess]);

  const launchConnection = useCallback(async (
    launchOptions?: tLaunchConnectionOptions | SyntheticEvent,
  ) => {
    if (pendingRef.current) return;
    const path = readEmbeddedSignupPath(launchOptions);
    const pinPopupToLeftEdge = readPinPopupToLeftEdge(launchOptions);
    pendingRef.current = true;
    // Drop any sessionInfo captured by a previous popup so a stale waba_id
    // cannot leak into this attempt.
    resetSessionInfo();
    // INT-1216: open a correlation id for this attempt. Meta's session_id only
    // arrives on error reports, so abandonments have no identifier of their own.
    beginEmbeddedSignupAttempt(surface, path);
    analytics?.onStarted?.(path);
    leaveSimulation();
    dispatch({ type: 'START_CONNECTING', path });
    // The connecting phase is not persisted, so the entry still describes the
    // previous failure: drop its cause, which no longer describes this attempt.
    if (workspaceId) dropStoredFailureCause(workspaceId);

    try {
      const isSdkReady = await waitForFacebookSDK(sdkTimeoutRef);
      if (!mountedRef.current) {
        pendingRef.current = false;
        finalizeEmbeddedSignupAttempt();
        return;
      }
      if (!isSdkReady) {
        // The limit ended the attempt before Meta's window could open; an SDK
        // that arrives later finds this attempt already over.
        pendingRef.current = false;
        finalizeEmbeddedSignupAttempt();
        const timeoutError = new Error(
          `Facebook SDK not ready after ${FACEBOOK_SDK_READY_TIMEOUT_MS}ms`,
        );
        LLog.error(
          {
            component: LOG_COMPONENTS.UI,
            method: 'whatsapp',
            operation: LOG_OPERATIONS.UI_INTERACTION,
          },
          'Facebook SDK did not load before the wait limit',
          timeoutError,
        );
        analytics?.onError?.('sdk_timeout', timeoutError);
        dispatch({
          type: 'FAIL_ERROR',
          error: timeoutError.message,
          errorCode: WHATSAPP_SDK_TIMEOUT_ERROR_CODE,
        });
        return;
      }

      // FB.login opens its popup synchronously, so the pinned window.open is in
      // place exactly while the SDK calls it.
      const login = (
        handleLoginResponse: (response: tFBLoginResponse) => void,
        loginOptions: tEmbeddedSignupLoginOptions,
      ) =>
        pinPopupToLeftEdge
          ? withPopupPinnedToLeftEdge(() =>
              FB.login(handleLoginResponse, loginOptions),
            )
          : FB.login(handleLoginResponse, loginOptions);

      login(
        (response: tFBLoginResponse) => {
          if (!mountedRef.current) {
            pendingRef.current = false;
            finalizeEmbeddedSignupAttempt();
            // The window closing is observed here even with the consumer gone,
            // so the failure is still reported; a code is not exchanged.
            if (!response.authResponse?.code)
              analytics?.onError?.('popup_closed');
            return;
          }
          // Outer try/finally guarantees pendingRef is released even if the
          // callback body throws synchronously (e.g. service constructs a
          // malformed URL when workspaceId is empty) — the FB SDK swallows
          // synchronous throws from this callback so without this the retry
          // button stays disabled until remount.
          try {
            const code = response.authResponse?.code;

            if (code) {
              dispatch({ type: 'META_RESPONDED' });
              const exchangeTimeout = new Promise<never>((_, reject) => {
                exchangeTimeoutRef.current = setTimeout(() => {
                  exchangeTimeoutRef.current = null;
                  reject(new WapiExchangeTimeoutError(AUTHENTICATE_TIMEOUT_MS));
                }, AUTHENTICATE_TIMEOUT_MS);
              });
              Promise.race([
                DashboardService.authenticateWhatsappAPI({
                  code,
                  workspaceId: workspaceId ?? '',
                  wabaId: sessionInfoRef.current?.wabaId,
                  phoneNumberId: sessionInfoRef.current?.phoneNumberId,
                }),
                exchangeTimeout,
              ])
                .then((body) => {
                  clearExchangeTimeout(exchangeTimeoutRef);
                  finalizeEmbeddedSignupAttempt();
                  // INT-1516: a 200 is not "connected" by itself; the body
                  // says what WBS decided about the record.
                  const { verdict, reason } = readConnectVerdict(body);
                  // Only an operational record makes the announcements whose
                  // socket event refreshes the integrations. A held, failed or
                  // superseded one announces nothing, so ask for them here,
                  // even when this dialog has already closed.
                  if (verdict !== 'operational') {
                    void refetchIntegrationsRef.current();
                  }
                  if (!mountedRef.current) return;
                  pendingRef.current = false;
                  switch (verdict) {
                    case 'operational':
                      // The one branch that observed the backend confirm the
                      // connection, so the only place the outcome-named
                      // success event may fire.
                      analytics?.onSuccess?.();
                      dispatch({ type: 'SUCCESS' });
                      return;
                    case 'failed':
                      dispatch({ type: 'FAIL_ACTIVATION', reason });
                      return;
                    case 'held':
                      dispatch({ type: 'VERIFY', reason });
                      return;
                    case 'superseded':
                      // A concurrent reconnect retired this record before the
                      // response was built. Hold until the refetch above shows
                      // what is live now.
                      dispatch({ type: 'VERIFY', reason: null });
                      return;
                  }
                })
                .catch((err) => {
                  // Terminal for the attempt whether or not we are still
                  // mounted — including the AUTHENTICATE_TIMEOUT_MS rejection.
                  clearExchangeTimeout(exchangeTimeoutRef);
                  finalizeEmbeddedSignupAttempt();
                  const wrapped = err instanceof Error ? err : new Error(String(err));
                  // INT-914: surface the backend's stable error code (the
                  // envelope's `code` field, e.g. wba-10073 "no WABA in
                  // token") so the failure screen can render actionable copy.
                  // The optional chain matters INSIDE the axios branch too: a
                  // timeout (ECONNABORTED) is an AxiosError whose `response`
                  // is undefined.
                  const errorCode = isAxiosError(err) ? err.response?.data?.code : undefined;
                  LLog.error(
                    {
                      component: LOG_COMPONENTS.API,
                      method: 'whatsapp',
                      operation: LOG_OPERATIONS.API_CALL,
                    },
                    'Error during WhatsApp API authentication',
                    wrapped,
                  );
                  // This branch observed the failed exchange, so it reports it
                  // even when the consumer already unmounted.
                  analytics?.onError?.('api_error', wrapped);
                  if (!mountedRef.current) return;
                  pendingRef.current = false;
                  dispatch({
                    type: 'FAIL_ERROR',
                    error: wrapped.message,
                    errorCode: typeof errorCode === 'string' ? errorCode : undefined,
                  });
                });
            } else {
              pendingRef.current = false;
              // The abandonment CANCEL has already been recorded by the
              // telemetry listener at this point; finalize so a late duplicate
              // cannot be counted twice.
              finalizeEmbeddedSignupAttempt();
              analytics?.onError?.('popup_closed');
              dispatch({ type: 'FAIL_CLOSED' });
            }
          } catch (callbackError) {
            pendingRef.current = false;
            finalizeEmbeddedSignupAttempt();
            const wrapped =
              callbackError instanceof Error ? callbackError : new Error(String(callbackError));
            LLog.error(
              {
                component: LOG_COMPONENTS.UI,
                method: 'whatsapp',
                operation: LOG_OPERATIONS.UI_INTERACTION,
              },
              'Synchronous throw inside FB.login callback',
              wrapped,
            );
            analytics?.onError?.('sdk_error', wrapped);
            dispatch({ type: 'FAIL_ERROR', error: wrapped.message });
          }
        },
        buildEmbeddedSignupLoginOptions({ path }),
      );
    } catch (error) {
      pendingRef.current = false;
      finalizeEmbeddedSignupAttempt();
      if (!mountedRef.current) return;
      const wrapped = error instanceof Error ? error : new Error(String(error));
      LLog.error(
        {
          component: LOG_COMPONENTS.UI,
          method: 'whatsapp',
          operation: LOG_OPERATIONS.UI_INTERACTION,
        },
        'Error launching WhatsApp Embedded sign up',
        wrapped,
      );
      analytics?.onError?.('sdk_error', wrapped);
      dispatch({ type: 'FAIL_ERROR', error: wrapped.message });
    }
  }, [
    workspaceId,
    analytics,
    resetSessionInfo,
    sessionInfoRef,
    surface,
    leaveSimulation,
  ]);

  const skip = useCallback(() => {
    analytics?.onSkipped?.();
    dispatch({ type: 'SKIP' });
  }, [analytics]);

  // Leaving a simulation is the whole reset: the stored entry is the real
  // attempt, not the screen that was previewed.
  const reset = useCallback(() => {
    if (leaveSimulation()) return;
    if (workspaceId) clearWapiState(workspaceId);
    dispatch({ type: 'RESET' });
  }, [workspaceId, leaveSimulation]);

  const backToConnect = useCallback(() => {
    dispatch({ type: 'BACK_TO_CONNECT' });
  }, []);

  // Previews a screen without a real attempt, while the screen simulator is
  // enabled. The mark goes last, after every action that reaches the screen.
  // Ignored while a real attempt is in flight: its result would land on the
  // simulated state and be dropped as fake. Returns whether the screen shows.
  const __devSimulate = useCallback((scenario: tWapiDevScenario): boolean => {
    if (!IS_WAPI_WIZARD_SIMULATOR_ENABLED || pendingRef.current) return false;
    dispatchDevScenario(dispatch, scenario);
    dispatch({ type: 'MARK_SIMULATED' });
    return true;
  }, []);

  return {
    state,
    launchConnection,
    retry: launchConnection,
    skip,
    reset,
    backToConnect,
    __devSimulate,
  } as const;
}
